import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { pluralize } from "@/helpers/utils";
import useLoadOptions from "@/hooks/useLoadOptions";
import CompanyStatus, {
  CompanyStatusFilters,
} from "@/interfaces/CompanyStatus";
import { IModalMethods } from "@/interfaces/Modal";
import { useUnDispatchUserCompaniesMutation } from "@/services/users/companies";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { toast } from "react-toastify";

interface UnDispatchForm {
  statuses: CompanyStatus[];
}

interface UnDispatchCompaniesModalMethods extends IModalMethods {
  showOrHiddenModal: (userId?: string) => void;
}

const UnDispatchCompaniesModal: ForwardRefRenderFunction<
  UnDispatchCompaniesModalMethods
> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [userId, setUserId] = useState<string | undefined>(undefined);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UnDispatchForm>({
    defaultValues: {
      statuses: [],
    },
  });

  const [unDispatchUserCompaniesAsync] = useUnDispatchUserCompaniesMutation();
  const { loadCompanyStatusesOptions } = useLoadOptions();

  const defaultAdditional = useMemo<CompanyStatusFilters>(
    () => ({
      pageIndex: 0,
      withMetadataForUserId: userId,
    }),
    [userId]
  );

  const showOrHiddenModal = (id?: string) => {
    if (id) {
      setUserId(id);
    } else {
      setUserId(undefined);
      reset();
    }
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<UnDispatchForm> = async ({ statuses }) => {
    if (!userId) return;

    const statusIds = statuses.map((status) => status.id);

    toast.promise(
      unDispatchUserCompaniesAsync({ userId, statusIds }).unwrap(),
      {
        pending: "Détachement des entreprises en cours...",
        success: {
          render({ data }) {
            reset();
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                {data?.deletedCount} entreprise(s) détachée(s) avec succès 🤩
              </p>
            );
          },
        },
        error: "Une erreur est survenue lors du détachement.",
      }
    );
  };

  const onError: SubmitErrorHandler<UnDispatchForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Détacher les entreprises par statut"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Statuts d'entreprise : <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              control={control}
              name="statuses"
              rules={{
                required: "Veuillez sélectionner au moins un statut",
              }}
              render={({ field: { value, onChange } }) => (
                <SelectPaginateAsync
                  loadOptions={loadCompanyStatusesOptions}
                  getOptionLabel={(option) => {
                    const companiesCount =
                      option.$extras?.companiesCountForUser ?? 0;

                    return `${option.name} (${companiesCount} ${pluralize(
                      "entreprise",
                      companiesCount
                    )})`;
                  }}
                  getOptionValue={(option) => option.id.toString()}
                  isMulti
                  isClearable
                  isDisabled={!userId}
                  value={value}
                  onChange={onChange}
                  additional={defaultAdditional}
                  aria-invalid={!!errors.statuses}
                  placeholder="Sélectionnez les statuts..."
                />
              )}
            />
            {errors.statuses && (
              <div className="invalid-feedback d-block">
                {errors.statuses.message}
              </div>
            )}
            <Form.Text className="text-muted">
              Toutes les entreprises avec ces statuts seront détachées de cet
              utilisateur
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(UnDispatchCompaniesModal);
